"use client";

import { useMemo } from "react";
import { Check, HelpCircle, Minus, Plus } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Switch } from "~/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "~/components/ui/tooltip";
import { cn } from "~/lib/utils";
import { formatHirePrice, formatHireTotalPrice } from "~/lib/marketplace/hire-pricing";
import {
  AGENT_TRAINING_ADVANCED_MONTHLY_CENTS,
  AGENT_TRAINING_BASIC_MONTHLY_CENTS,
  INCLUDED_FREE_CHAT_CONVERSATIONS,
  INCLUDED_FREE_PHONE_MINUTES,
  INCLUDED_FREE_SMS,
  INCLUDED_FREE_VIDEO_MINUTES,
  INCLUDED_FREE_WELCOME_VIDEO_MINUTES,
  RECOMMENDATIONS_APPOINTMENTS_OPTIONS,
  RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS,
  RECOMMENDATIONS_EMR_OPTIONS,
  RECOMMENDATIONS_LOCATION_MONTHLY_CENTS,
  RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS,
  SKILL_BROWSER_CALLS_MONTHLY_CENTS,
  SKILL_EMAIL_MONTHLY_CENTS,
  SKILL_LIVE_CHAT_MONTHLY_CENTS,
  SKILL_MESSENGER_MONTHLY_CENTS,
  SKILL_PHONE_CALLS_MONTHLY_CENTS,
  SKILL_TEXT_MESSAGES_MONTHLY_CENTS,
  SKILL_VIDEO_CALLS_MONTHLY_CENTS,
  SKILL_WELCOME_VIDEO_MONTHLY_CENTS,
  SKILL_WHATSAPP_MONTHLY_CENTS,
  USAGE_PHONE_MINUTE_CENTS,
  USAGE_SMS_CENTS,
  USAGE_VIDEO_MINUTE_CENTS,
  USAGE_WELCOME_VIDEO_MINUTE_CENTS,
  computeDepartmentCostCents,
  computeLocationCostCents,
  computeRecommendationsTotalCents,
  computeTeamMemberCostCents,
  emrIntegrationPriceCents,
  formatRecommendationsEmrLabel,
  formatRecommendationsSchedulingLabel,
  isEmrIntegrationId,
  schedulingIntegrationPriceCents,
  type HireRecommendationsSelection,
  type MarketplaceEmrIntegrationId,
} from "~/lib/marketplace/hire-recommendations-pricing";
import type { MarketplaceSchedulingIntegrationId } from "~/lib/marketplace/hire-pricing";
import type { HireResponsibilityPreviewId } from "~/lib/marketplace/hire-responsibility-widget-tab";
import type { AiEmployeePlanId } from "~/lib/siteglue/ai-employee-plans";
import {
  AI_EMPLOYEE_PLANS,
  aiEmployeePlanPriceCents,
  formatAiEmployeePlanHeaderLabel,
} from "~/lib/siteglue/ai-employee-plans";
import { planAllowsFeature, type PlanFeatureKey } from "~/lib/siteglue/plan-presets";

export type HirePricingTeamContact = {
  name: string;
  email?: string;
};

type HireRecommendationsStepProps = {
  businessName: string;
  selection: HireRecommendationsSelection;
  onChange: (next: HireRecommendationsSelection) => void;
  className?: string;
  /** Section heading (default: Agent Pricing). */
  title?: string;
  /** Single-column layout for narrow sidebars (e.g. template profile). */
  compact?: boolean;
  /** Show monthly total in the step header (default true). */
  showTotal?: boolean;
  /** Hide team member and location steppers (shown on Company Profile step instead). */
  hideTeamAndLocations?: boolean;
  /** Hide section title and subtitle (when parent provides its own heading). */
  hideTitle?: boolean;
  /** Left column group heading (default: Agent Profile). */
  agentProfileTitle?: string;
  /** Right column group heading (default: Company Profile). */
  companyProfileTitle?: string;
  /** Show help tooltips on pricing rows (default: false). */
  showTooltips?: boolean;
  /** Optional footnote below the pricing grid (e.g. pricing page disclaimer). */
  footnote?: string;
  /** Open the live widget preview on the tab matching a responsibility. */
  onResponsibilityPreview?: (id: HireResponsibilityPreviewId) => void;
  employerContact?: HirePricingTeamContact;
  leadTeamMember?: HirePricingTeamContact;
  /** Show a single section in wizard mode (default: all). */
  section?: "all" | "responsibilities" | "training" | "tools";
  /**
   * SiteGlue plan ceiling — disables toggles for features above this plan.
   * When set, shows a plan banner and upgrade hint.
   */
  planCeilingId?: AiEmployeePlanId | null;
  /** Called when user wants to upgrade to the next plan. */
  onRequestPlanUpgrade?: () => void;
  /** Called when user wants to view the adjacent SiteGlue plan tier. */
  onViewAdjacentPlan?: () => void;
  /** Label for the single adjacent-plan button (e.g. "View Associate"). */
  viewAdjacentPlanLabel?: string | null;
  /** Hide per-unit rates (responsibilities, org steppers, tool add-ons) — plan-based SiteGlue UI. */
  hideUnitPricing?: boolean;
  /** Subsection heading for channel toggles (default: Responsibilities). */
  channelsSectionTitle?: string;
  /** Subtitle under the step title (default: responsibilities copy). */
  stepDescription?: string;
};

export function InfoTip({ text }: { text: string }) {
  return (
    <Tooltip delayDuration={150}>
      <TooltipTrigger asChild>
        <span
          className="ml-1.5 inline-flex cursor-help items-center text-slate-400 hover:text-slate-600"
          aria-label="More info"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[260px] text-sm">
        {text}
      </TooltipContent>
    </Tooltip>
  );
}

export function LabelWithTip({
  label,
  tooltip,
  showTooltip,
}: {
  label: string;
  tooltip?: string;
  showTooltip?: boolean;
}) {
  return (
    <span className="inline-flex items-center">
      {label}
      {showTooltip && tooltip ? <InfoTip text={tooltip} /> : null}
    </span>
  );
}

function SkillToggle({
  label,
  priceCents,
  priceDetail,
  checked,
  onCheckedChange,
  disabled,
  tooltip,
  showTooltip,
  onPreview,
  hideUnitPricing,
}: {
  label: string;
  priceCents: number;
  priceDetail?: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  tooltip?: string;
  showTooltip?: boolean;
  onPreview?: () => void;
  hideUnitPricing?: boolean;
}) {
  const priceLabel =
    priceDetail ??
    (priceCents === 0 ? "Free" : `${formatHirePrice(priceCents)}/mo`);

  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <button
        type="button"
        className="min-w-0 flex-1 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        onClick={() => onPreview?.()}
        disabled={!onPreview}
      >
        <p className="text-sm font-medium text-slate-900">
          <LabelWithTip label={label} tooltip={tooltip} showTooltip={showTooltip} />
        </p>
        {!hideUnitPricing ? (
          <p className="text-xs text-slate-500">{priceLabel}</p>
        ) : null}
      </button>
      <Switch
        checked={checked}
        onCheckedChange={(next) => {
          onCheckedChange(next);
          if (next) onPreview?.();
        }}
        disabled={disabled}
        aria-label={label}
      />
    </div>
  );
}

function TeamContactLine({
  label,
  contact,
}: {
  label: string;
  contact: HirePricingTeamContact;
}) {
  return (
    <div className="rounded-md border border-slate-100 bg-white px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-slate-900">{contact.name || "—"}</p>
      {contact.email ? (
        <p className="text-xs text-slate-500">{contact.email}</p>
      ) : null}
    </div>
  );
}

function ProfileGroup({
  title,
  children,
  /** When true, omit the group heading (parent already shows the section title). */
  hideHeading = false,
}: {
  title: string;
  children: React.ReactNode;
  hideHeading?: boolean;
}) {
  return (
    <div className="space-y-5">
      {!hideHeading ? (
        <h4 className="border-b border-slate-200 pb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
          {title}
        </h4>
      ) : null}
      <div className="space-y-5">{children}</div>
    </div>
  );
}

export function HireRecommendationsStep({
  businessName,
  selection,
  onChange,
  className,
  title = "Agent Pricing",
  compact = false,
  showTotal = true,
  hideTeamAndLocations = false,
  hideTitle = false,
  agentProfileTitle = "Agent Profile",
  companyProfileTitle = "Company Profile",
  showTooltips = false,
  footnote,
  onResponsibilityPreview,
  employerContact,
  leadTeamMember,
  section = "all",
  planCeilingId = null,
  onRequestPlanUpgrade,
  onViewAdjacentPlan,
  viewAdjacentPlanLabel,
  hideUnitPricing = false,
  channelsSectionTitle = "Responsibilities",
  stepDescription,
}: HireRecommendationsStepProps) {
  const showResponsibilities = section === "all" || section === "responsibilities";
  const showTraining = section === "all" || section === "training";
  const showTools = section === "all" || section === "tools";
  const showOrgSections = section === "all" && !hideTeamAndLocations;
  const isSingleSection = section !== "all";
  const totalCents = useMemo(() => {
    if (planCeilingId) {
      return aiEmployeePlanPriceCents(planCeilingId);
    }
    return computeRecommendationsTotalCents(selection);
  }, [selection, planCeilingId]);

  const totalLabel = planCeilingId
    ? formatAiEmployeePlanHeaderLabel(planCeilingId)
    : formatHireTotalPrice(totalCents);

  const locationCostCents = computeLocationCostCents(selection.locationCount);
  const departmentCostCents = computeDepartmentCostCents(selection.departmentCount);
  const teamCostCents = computeTeamMemberCostCents(selection.teamMemberCount);
  const appointmentsCostCents =
    selection.appointmentsEnabled && selection.schedulingIntegrationId !== "none"
      ? schedulingIntegrationPriceCents(selection.schedulingIntegrationId)
      : 0;
  const emrCostCents =
    selection.emrIntegrationEnabled && selection.emrIntegrationId !== "none"
      ? emrIntegrationPriceCents(selection.emrIntegrationId)
      : 0;

  const featureLocked = (feature: PlanFeatureKey) =>
    Boolean(planCeilingId) && !planAllowsFeature(planCeilingId!, feature);

  const planMeta = planCeilingId
    ? AI_EMPLOYEE_PLANS.find((p) => p.id === planCeilingId)
    : null;
  const nextPlanId: AiEmployeePlanId | null =
    planCeilingId === "intern"
      ? "associate"
      : planCeilingId === "associate"
        ? "professional"
        : planCeilingId === "professional"
          ? "enterprise"
          : null;

  const update = (patch: Partial<HireRecommendationsSelection>) => {
    onChange({ ...selection, ...patch });
  };

  const preview = (id: HireResponsibilityPreviewId) => {
    onResponsibilityPreview?.(id);
  };

  const skillPricingProps = hideUnitPricing ? { hideUnitPricing: true as const } : {};

  const resolvedCompanyTitle = hideTeamAndLocations ? "Integrations" : companyProfileTitle;

  const resolvedStepDescription =
    stepDescription ??
    `Adjust responsibilities, training, and tools for ${businessName}`;

  return (
    <TooltipProvider>
    <section className={cn("space-y-5", className)}>
      {planMeta ? (
        <div className="flex flex-col gap-3 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-950">
          <div>
            <p className="font-semibold">Plan: {planMeta.name}</p>
            <p className="text-xs text-sky-800/90">
              Features above this plan are locked. View a plan to explore channels and tools.
            </p>
          </div>
          {onViewAdjacentPlan && viewAdjacentPlanLabel ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="w-fit border-sky-300 bg-white text-sky-950 hover:bg-sky-100"
              onClick={onViewAdjacentPlan}
            >
              {viewAdjacentPlanLabel}
            </Button>
          ) : nextPlanId && onRequestPlanUpgrade ? (
            <Button type="button" size="sm" variant="outline" onClick={onRequestPlanUpgrade}>
              View {AI_EMPLOYEE_PLANS.find((p) => p.id === nextPlanId)?.name ?? "next plan"}
            </Button>
          ) : null}
        </div>
      ) : null}
      <div
        className={cn(
          "rounded-lg border border-slate-200 bg-slate-50/60",
          compact ? "p-3" : "p-5",
        )}
      >
        <div className={cn("mb-5 flex items-start justify-between gap-4", compact && "mb-3", hideTitle && "hidden")}>
          <div>
            <h3 className={cn("font-semibold text-slate-900", compact ? "text-base" : "text-lg")}>
              {title}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {resolvedStepDescription}
            </p>
          </div>
          {showTotal ? (
            <p className={cn("shrink-0 font-bold text-slate-900", compact ? "text-lg" : "text-xl")}>
              {totalLabel}
            </p>
          ) : null}
        </div>

        <div
          className={cn(
            "grid gap-8",
            compact || isSingleSection ? "grid-cols-1 gap-5" : "lg:grid-cols-2",
          )}
        >
          {(showResponsibilities || showTraining) && (
          <ProfileGroup
            title={isSingleSection ? title : agentProfileTitle}
            hideHeading={isSingleSection}
          >
            {showResponsibilities ? (
            <div>
              {/* When channels-only, the step title is the section heading. */}
              {!isSingleSection ? (
                <h4 className="mb-2 text-sm font-semibold text-slate-900">
                  {channelsSectionTitle}
                </h4>
              ) : null}
              <div className="rounded-md border border-slate-100 bg-white px-3 py-1">
                <SkillToggle
                  {...skillPricingProps}
                  label="Live Chat"
                  priceCents={SKILL_LIVE_CHAT_MONTHLY_CENTS}
                  priceDetail="Unlimited · Free"
                  checked={selection.liveChat}
                  onCheckedChange={(liveChat) =>
                    update({
                      liveChat,
                      chatConversations: liveChat
                        ? Math.max(
                            selection.chatConversations,
                            INCLUDED_FREE_CHAT_CONVERSATIONS,
                          )
                        : selection.chatConversations,
                    })
                  }
                  disabled
                  showTooltip={showTooltips}
                  tooltip="Website chat widget included with every Inbound GPT Agent at no extra cost."
                  onPreview={() => preview("liveChat")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Voice (WebRTC)"
                  priceCents={planCeilingId ? 0 : SKILL_BROWSER_CALLS_MONTHLY_CENTS}
                  priceDetail={
                    planCeilingId
                      ? planCeilingId === "intern"
                        ? "View Associate"
                        : "Included in plan"
                      : "Unlimited · Free"
                  }
                  checked={selection.browserCalls}
                  onCheckedChange={(browserCalls) => update({ browserCalls })}
                  disabled={featureLocked("browserCalls")}
                  showTooltip={showTooltips}
                  tooltip="WebRTC voice calls from your website Talk tab."
                  onPreview={() => preview("browserCalls")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Text Messages"
                  priceCents={SKILL_TEXT_MESSAGES_MONTHLY_CENTS}
                  priceDetail={`First ${INCLUDED_FREE_SMS} SMS free, then ${formatHirePrice(USAGE_SMS_CENTS)}/SMS`}
                  checked={selection.textMessages}
                  onCheckedChange={(textMessages) =>
                    update({
                      textMessages,
                      smsCount: textMessages
                        ? Math.max(selection.smsCount, INCLUDED_FREE_SMS)
                        : 0,
                    })
                  }
                  disabled={featureLocked("textMessages")}
                  showTooltip={showTooltips}
                  tooltip="Two-way SMS with your dedicated agent number. First 100 messages each month are included; additional messages are billed per SMS."
                  onPreview={() => preview("textMessages")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Email"
                  priceCents={SKILL_EMAIL_MONTHLY_CENTS}
                  priceDetail="Unlimited"
                  checked={selection.email}
                  onCheckedChange={(email) => update({ email })}
                  disabled={featureLocked("email")}
                  showTooltip={showTooltips}
                  tooltip="Dedicated agent email for inbound and outbound conversations with unlimited volume."
                  onPreview={() => preview("email")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Video Calls"
                  priceCents={SKILL_VIDEO_CALLS_MONTHLY_CENTS}
                  priceDetail={`First ${INCLUDED_FREE_VIDEO_MINUTES} min free, then ${formatHirePrice(USAGE_VIDEO_MINUTE_CENTS)}/min`}
                  checked={selection.videoCalls}
                  onCheckedChange={(videoCalls) =>
                    update({
                      videoCalls,
                      browserCalls: videoCalls ? true : selection.browserCalls,
                      videoCallMinutes: videoCalls
                        ? Math.max(
                            selection.videoCallMinutes,
                            INCLUDED_FREE_VIDEO_MINUTES,
                          )
                        : 0,
                    })
                  }
                  disabled={featureLocked("videoCalls")}
                  showTooltip={showTooltips}
                  tooltip="Browser-based Anam video calls from your website. First 20 minutes each month are free."
                  onPreview={() => preview("videoCalls")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Phone Calls"
                  priceCents={SKILL_PHONE_CALLS_MONTHLY_CENTS}
                  priceDetail={`First ${INCLUDED_FREE_PHONE_MINUTES} min free, then ${formatHirePrice(USAGE_PHONE_MINUTE_CENTS)}/min`}
                  checked={selection.phoneCalls}
                  onCheckedChange={(phoneCalls) =>
                    update({
                      phoneCalls,
                      phoneCallMinutes: phoneCalls
                        ? Math.max(
                            selection.phoneCallMinutes,
                            INCLUDED_FREE_PHONE_MINUTES,
                          )
                        : 0,
                    })
                  }
                  disabled={featureLocked("phoneCalls")}
                  showTooltip={showTooltips}
                  tooltip="AI phone receptionist with inbound and outbound calling. First 20 minutes each month are free."
                  onPreview={() => preview("phoneCalls")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Welcome Video"
                  priceCents={SKILL_WELCOME_VIDEO_MONTHLY_CENTS}
                  priceDetail={`First ${INCLUDED_FREE_WELCOME_VIDEO_MINUTES} min free, then ${formatHirePrice(USAGE_WELCOME_VIDEO_MINUTE_CENTS)}/min`}
                  checked={selection.welcomeVideo}
                  onCheckedChange={(welcomeVideo) =>
                    update({
                      welcomeVideo,
                      welcomeVideoMinutes: welcomeVideo
                        ? Math.max(
                            selection.welcomeVideoMinutes,
                            INCLUDED_FREE_WELCOME_VIDEO_MINUTES,
                          )
                        : 0,
                    })
                  }
                  showTooltip={showTooltips}
                  tooltip="Prerecorded welcome video from your lead team member on your website. First 20 minutes each month are free."
                  onPreview={() => preview("welcomeVideo")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Messenger"
                  priceCents={SKILL_MESSENGER_MONTHLY_CENTS}
                  priceDetail="Unlimited · Free"
                  checked={selection.messenger}
                  onCheckedChange={(messenger) => update({ messenger })}
                  disabled={featureLocked("messenger")}
                  showTooltip={showTooltips}
                  tooltip="Facebook Messenger conversations handled by your agent at no additional cost."
                  onPreview={() => preview("messenger")}
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="WhatsApp"
                  priceCents={SKILL_WHATSAPP_MONTHLY_CENTS}
                  priceDetail="Unlimited · Free"
                  checked={selection.whatsapp}
                  onCheckedChange={(whatsapp) => update({ whatsapp })}
                  disabled={featureLocked("whatsapp")}
                  showTooltip={showTooltips}
                  tooltip="WhatsApp Business conversations handled by your agent at no additional cost."
                  onPreview={() => preview("whatsapp")}
                />
              </div>
            </div>
            ) : null}

            {showTraining ? (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-slate-900">Training</h4>
              <div className="rounded-md border border-slate-100 bg-white px-3 py-1">
                <SkillToggle
                  {...skillPricingProps}
                  label="Basic"
                  priceCents={AGENT_TRAINING_BASIC_MONTHLY_CENTS}
                  checked={selection.agentTrainingBasic}
                  onCheckedChange={(agentTrainingBasic) => update({ agentTrainingBasic })}
                  disabled
                  showTooltip={showTooltips}
                  tooltip="Core training from your website and business profile."
                />
                <SkillToggle
                  {...skillPricingProps}
                  label="Advanced"
                  priceCents={AGENT_TRAINING_ADVANCED_MONTHLY_CENTS}
                  checked={selection.agentTrainingAdvanced}
                  onCheckedChange={(agentTrainingAdvanced) =>
                    update({ agentTrainingAdvanced })
                  }
                  disabled={featureLocked("agentTrainingAdvanced")}
                  showTooltip={showTooltips}
                  tooltip="Extended knowledge base with uploaded documents, FAQs, and custom training content."
                />
              </div>
            </div>
            ) : null}
          </ProfileGroup>
          )}

          {(showOrgSections || showTools) && (
          <ProfileGroup title={resolvedCompanyTitle}>
            {showOrgSections ? (
            <>
            <div>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900">
                  <LabelWithTip
                    label="Team Members"
                    showTooltip={showTooltips}
                    tooltip="People who receive leads, handoffs, and notifications. Your account owner and first lead team member are included free."
                  />
                </h4>
                {!hideUnitPricing ? (
                  <span className="text-xs text-slate-500">
                    First free, then {formatHirePrice(RECOMMENDATIONS_TEAM_MEMBER_MONTHLY_CENTS)}
                    /member/mo
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() =>
                      update({ teamMemberCount: Math.max(1, selection.teamMemberCount - 1) })
                    }
                    disabled={selection.teamMemberCount <= 1}
                    aria-label="Decrease team members"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                    {selection.teamMemberCount}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => update({ teamMemberCount: selection.teamMemberCount + 1 })}
                    aria-label="Increase team members"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {!hideUnitPricing ? (
                  <span className="text-sm font-medium text-slate-700">
                    {teamCostCents === 0 ? "Free" : `${formatHirePrice(teamCostCents)}/mo`}
                  </span>
                ) : null}
              </div>
              {employerContact || leadTeamMember ? (
                <div className="mt-3 space-y-2">
                  {employerContact ? (
                    <TeamContactLine label="Employer" contact={employerContact} />
                  ) : null}
                  {leadTeamMember ? (
                    <TeamContactLine label="Lead Team Member" contact={leadTeamMember} />
                  ) : null}
                </div>
              ) : (
                <p className="mt-2 text-xs text-slate-500">{selection.teamMembersLabel}</p>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900">
                  <LabelWithTip
                    label="Locations"
                    showTooltip={showTooltips}
                    tooltip="Physical offices or service areas your agent supports. Your first location is included free."
                  />
                </h4>
                {!hideUnitPricing ? (
                  <span className="text-xs text-slate-500">
                    First free, then {formatHirePrice(RECOMMENDATIONS_LOCATION_MONTHLY_CENTS)}
                    /location/mo
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() =>
                      update({ locationCount: Math.max(1, selection.locationCount - 1) })
                    }
                    disabled={selection.locationCount <= 1}
                    aria-label="Decrease locations"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                    {selection.locationCount}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() => update({ locationCount: selection.locationCount + 1 })}
                    aria-label="Increase locations"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {!hideUnitPricing ? (
                  <span className="text-sm font-medium text-slate-700">
                    {locationCostCents === 0 ? "Free" : `${formatHirePrice(locationCostCents)}/mo`}
                  </span>
                ) : null}
              </div>
              {selection.locationList.length > 0 ? (
                <ul className="mt-3 space-y-1.5">
                  {selection.locationList.slice(0, selection.locationCount).map((loc) => (
                    <li
                      key={loc}
                      className="flex items-start gap-2 text-sm text-slate-700"
                    >
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                      <span>{loc}</span>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <div>
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900">
                  <LabelWithTip
                    label="Departments"
                    showTooltip={showTooltips}
                    tooltip="Business departments your agent can route conversations to, such as billing, scheduling, or clinical teams. Your first department is included free."
                  />
                </h4>
                {!hideUnitPricing ? (
                  <span className="text-xs text-slate-500">
                    First free, then {formatHirePrice(RECOMMENDATIONS_DEPARTMENT_MONTHLY_CENTS)}
                    /department/mo
                  </span>
                ) : null}
              </div>
              <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2">
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() =>
                      update({
                        departmentCount: Math.max(1, selection.departmentCount - 1),
                      })
                    }
                    disabled={selection.departmentCount <= 1}
                    aria-label="Decrease departments"
                  >
                    <Minus className="h-3.5 w-3.5" />
                  </Button>
                  <span className="min-w-[2rem] text-center text-sm font-semibold tabular-nums">
                    {selection.departmentCount}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={() =>
                      update({ departmentCount: selection.departmentCount + 1 })
                    }
                    aria-label="Increase departments"
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {!hideUnitPricing ? (
                  <span className="text-sm font-medium text-slate-700">
                    {departmentCostCents === 0
                      ? "Free"
                      : `${formatHirePrice(departmentCostCents)}/mo`}
                  </span>
                ) : null}
              </div>
            </div>
            </>
            ) : null}

            {showTools ? (
            <div>
              <h4 className="mb-2 text-sm font-semibold text-slate-900">Tools</h4>
              <div className="space-y-5 rounded-md border border-slate-100 bg-white px-3 py-3">
              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h5 className="text-sm font-medium text-slate-900">
                    <LabelWithTip
                      label="Appointments and Scheduling"
                      showTooltip={showTooltips}
                      tooltip="Connect Google Calendar, Calendly, or Zoom so your agent can book appointments during chat and phone conversations."
                    />
                  </h5>
                  <Switch
                    checked={selection.appointmentsEnabled}
                    onCheckedChange={(appointmentsEnabled) =>
                      update({
                        appointmentsEnabled,
                        schedulingIntegrationId:
                          appointmentsEnabled &&
                          (selection.schedulingIntegrationId === "none" ||
                            isEmrIntegrationId(selection.schedulingIntegrationId))
                            ? "google_calendar"
                            : selection.schedulingIntegrationId,
                      })
                    }
                    disabled={featureLocked("appointmentsEnabled")}
                    aria-label="Enable appointments and scheduling"
                  />
                </div>
                {selection.appointmentsEnabled ? (
                  <Select
                    value={
                      selection.schedulingIntegrationId === "none" ||
                      isEmrIntegrationId(selection.schedulingIntegrationId)
                        ? "google_calendar"
                        : selection.schedulingIntegrationId
                    }
                    onValueChange={(next) =>
                      update({
                        schedulingIntegrationId: next as MarketplaceSchedulingIntegrationId,
                      })
                    }
                  >
                    <SelectTrigger className="h-10 w-full text-sm">
                      <SelectValue placeholder="Select an integration" />
                    </SelectTrigger>
                    <SelectContent>
                      {RECOMMENDATIONS_APPOINTMENTS_OPTIONS.map((integration) => (
                        <SelectItem key={integration.id} value={integration.id}>
                          {formatRecommendationsSchedulingLabel(integration.id, {
                            hidePrice: hideUnitPricing,
                          })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                {selection.appointmentsEnabled &&
                !hideUnitPricing &&
                (planCeilingId || appointmentsCostCents > 0) ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {planCeilingId
                      ? "Included in Associate+ plans"
                      : `Tool add-on: ${formatHirePrice(appointmentsCostCents)}/mo`}
                  </p>
                ) : null}
              </div>

              <div>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <h5 className="text-sm font-medium text-slate-900">
                    <LabelWithTip
                      label="Practice Management System (EMR)"
                      showTooltip={showTooltips}
                      tooltip="Practice management and EMR systems for healthcare scheduling, patient lookup, and appointment booking."
                    />
                  </h5>
                  <Switch
                    checked={selection.emrIntegrationEnabled}
                    onCheckedChange={(emrIntegrationEnabled) =>
                      update({
                        emrIntegrationEnabled,
                        emrIntegrationId:
                          emrIntegrationEnabled && selection.emrIntegrationId === "none"
                            ? "juvonno"
                            : selection.emrIntegrationId,
                      })
                    }
                    disabled={featureLocked("emrIntegrationEnabled")}
                    aria-label="Enable practice management system integration"
                  />
                </div>
                {selection.emrIntegrationEnabled ? (
                  <Select
                    value={
                      selection.emrIntegrationId === "none"
                        ? "juvonno"
                        : selection.emrIntegrationId
                    }
                    onValueChange={(next) =>
                      update({
                        emrIntegrationId: next as MarketplaceEmrIntegrationId,
                      })
                    }
                  >
                    <SelectTrigger className="h-10 w-full text-sm">
                      <SelectValue placeholder="Select an EMR platform" />
                    </SelectTrigger>
                    <SelectContent>
                      {RECOMMENDATIONS_EMR_OPTIONS.map((integration) => (
                        <SelectItem key={integration.id} value={integration.id}>
                          {formatRecommendationsEmrLabel(integration.id, {
                            hidePrice: hideUnitPricing,
                          })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                {selection.emrIntegrationEnabled &&
                !hideUnitPricing &&
                (planCeilingId || emrCostCents > 0) ? (
                  <p className="mt-2 text-xs text-slate-500">
                    {planCeilingId
                      ? "Included in Professional+ plans"
                      : `Tool add-on: ${formatHirePrice(emrCostCents)}/mo`}
                  </p>
                ) : null}
              </div>
              </div>
            </div>
            ) : null}
          </ProfileGroup>
          )}
        </div>

        {footnote ? (
          <p className="mt-5 border-t border-slate-200 pt-4 text-sm text-muted-foreground">
            {footnote}
          </p>
        ) : null}
      </div>
    </section>
    </TooltipProvider>
  );
}
